import { useRef, useState } from 'react';
import { unlockAudio } from '@/audio/context';
import { useHotkeys } from '@/lib/useHotkeys';
import { buttonClass } from '@/ui/button';
import {
  CALIBRATION_CLICKS,
  CLICK_INTERVAL,
  LEAD_IN_CLICKS,
  MAX_OFFSET_MS,
  offsetFromTaps,
} from './calibration';
import styles from './SyncCalibrator.module.css';

interface SyncCalibratorProps {
  valueMs: number;
  onChange: (ms: number) => void;
}

type Phase = 'idle' | 'running' | 'done' | 'failed';

function scheduleClick(ctx: AudioContext, when: number) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.frequency.value = 1000;
  gain.gain.setValueAtTime(0.0001, when);
  gain.gain.exponentialRampToValueAtTime(0.6, when + 0.002);
  gain.gain.exponentialRampToValueAtTime(0.0001, when + 0.05);
  osc.connect(gain).connect(ctx.destination);
  osc.start(when);
  osc.stop(when + 0.06);
}

/**
 * Tap-along calibration of the visual sync offset (Settings → Playback sync). Useful for
 * Bluetooth headphones and other outputs whose delay the browser doesn't know.
 */
export function SyncCalibrator({ valueMs, onChange }: SyncCalibratorProps) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [tapCount, setTapCount] = useState(0);
  const session = useRef<{ ctx: AudioContext; clicks: number[]; taps: number[] } | null>(null);

  const start = async () => {
    const ctx = await unlockAudio();
    const first = ctx.currentTime + 0.5;
    const clicks = Array.from({ length: CALIBRATION_CLICKS }, (_, i) => first + i * CLICK_INTERVAL);
    clicks.forEach((t) => {
      scheduleClick(ctx, t);
    });
    session.current = { ctx, clicks, taps: [] };
    setTapCount(0);
    setPhase('running');
    const last = first + (CALIBRATION_CLICKS - 1) * CLICK_INTERVAL;
    const endMs = (last - ctx.currentTime + 0.6) * 1000;
    window.setTimeout(() => {
      const s = session.current;
      if (!s) return;
      // Some browsers report outputLatency as 0 (unknown); then use baseLatency.
      const reported = s.ctx.outputLatency > 0 ? s.ctx.outputLatency : s.ctx.baseLatency;
      const offset = offsetFromTaps(s.clicks, s.taps, reported);
      session.current = null;
      if (offset === null) {
        setPhase('failed');
        return;
      }
      onChange(offset);
      setPhase('done');
    }, endMs);
  };

  const tap = (eventTimeStamp: number) => {
    const s = session.current;
    if (!s) return;
    // Correct for the time between the key press and this handler running.
    s.taps.push(s.ctx.currentTime - (performance.now() - eventTimeStamp) / 1000);
    setTapCount(s.taps.length);
  };

  useHotkeys(
    {
      ' ': () => {
        tap(performance.now());
      },
    },
    phase === 'running',
  );

  return (
    <div className={styles.calibrator}>
      <p className={styles.help}>
        If the highlight runs ahead of or behind the music (common with Bluetooth headphones),
        calibrate: press Start, then tap along with the clicks.
      </p>
      <div className={styles.row}>
        {phase === 'running' ? (
          <button
            type="button"
            className={styles.tap}
            onPointerDown={(e) => {
              tap(e.timeStamp);
            }}
          >
            Tap with each click
            <span className={styles.tapCount}>
              {tapCount} taps · {CALIBRATION_CLICKS - LEAD_IN_CLICKS} counted clicks
            </span>
          </button>
        ) : (
          <button type="button" className={buttonClass('primary')} onClick={() => void start()}>
            {phase === 'idle' ? 'Start calibration' : 'Calibrate again'}
          </button>
        )}
        <span className={styles.status} role="status">
          {phase === 'done' && `Saved: ${valueMs > 0 ? '+' : ''}${valueMs} ms.`}
          {phase === 'failed' && 'Not enough taps were close to the clicks. Try again.'}
          {phase === 'running' && 'Listening… you can also press Space.'}
        </span>
      </div>
      <label className={styles.slider}>
        <span>
          Visual offset <b>{valueMs > 0 ? `+${valueMs}` : valueMs} ms</b>
        </span>
        <input
          type="range"
          min={-MAX_OFFSET_MS}
          max={MAX_OFFSET_MS}
          step={5}
          value={valueMs}
          onChange={(e) => {
            onChange(Number(e.target.value));
          }}
        />
        <span className={styles.scale}>
          <span>Earlier</span>
          <button
            type="button"
            className={styles.reset}
            onClick={() => {
              onChange(0);
            }}
          >
            Reset to 0
          </button>
          <span>Later</span>
        </span>
      </label>
    </div>
  );
}
